import { Download, ExternalLink } from 'lucide-react';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ClickableRow } from '@/components/list/ClickableRow';
import { COMPACT_ONLY, LIST_CONTAINER, WIDE_ONLY } from '@/components/list/list-columns';
import { RowActionsMenu, type RowAction } from '@/components/ui/row-actions-menu';
import { cn } from '@/lib/utils';
import { formatAmount } from '../money';
import { ReportPurpose } from './ReportPurpose';
import { ReportStatusBadge } from './ReportStatusBadge';
import type { ReportSummary } from './types';
import type { NotePdf } from './use-report-pdf';

const HEAD = 'px-3 py-2.5 font-semibold';

/**
 * A row's «···»: the note's PDF, when the row's `can.downloadPdf` allows it, including empty notes.
 */
function rowActions(t: TFunction<'expenses'>, report: ReportSummary, pdf: NotePdf): RowAction[] {
  if (report.can?.downloadPdf !== true) return [];
  const busy = pdf.busy?.id === report.id;
  return [
    {
      id: 'pdf-open',
      label: t('reports.pdf.openWide'),
      icon: ExternalLink,
      busy,
      onSelect: () => void pdf.run(report, 'inline'),
    },
    {
      id: 'pdf-download',
      label: t('reports.pdf.downloadWide'),
      icon: Download,
      busy,
      onSelect: () => void pdf.run(report, 'attachment'),
    },
  ];
}

function RowMenu({
  report,
  pdf,
  touch = false,
}: {
  readonly report: ReportSummary;
  readonly pdf: NotePdf;
  /** A phone's card: the trigger is a 44 px touch target (U16). */
  readonly touch?: boolean;
}) {
  const { t } = useTranslation('expenses');
  return (
    <RowActionsMenu
      label={t('reports.rowActions', { number: report.number })}
      actions={rowActions(t, report, pdf)}
      {...(touch ? { triggerClassName: 'size-11' } : {})}
    />
  );
}

/**
 * Desktop (spec §1.1): number, user, reason, expenses, total, reimbursable, status. Below 1180 px
 * of the list's own width (#952 H7: `list-columns.ts`) it keeps number, reason, total and status:
 * the person and the purpose go under the reason, the count and the reimbursable amount to the note.
 */
export function ReportsTable({
  items,
  showOwner,
  pdf,
}: {
  readonly items: readonly ReportSummary[];
  readonly showOwner: boolean;
  readonly pdf: NotePdf;
}) {
  const { t } = useTranslation('expenses');
  const navigate = useNavigate();
  return (
    <div
      className={cn(
        'border-border hidden overflow-x-auto rounded-xl border @min-[620px]/shell:block',
        LIST_CONTAINER,
      )}
    >
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{t('reports.title')}</caption>
        <thead className="bg-muted/50 text-muted-foreground text-left text-xs tracking-wide uppercase">
          <tr>
            <th scope="col" className={HEAD}>
              {t('reports.columns.number')}
            </th>
            {showOwner ? (
              <th scope="col" className={cn(HEAD, 'whitespace-nowrap', WIDE_ONLY)}>
                {t('reports.columns.owner')}
              </th>
            ) : null}
            <th scope="col" className={HEAD}>
              {t('reports.columns.title')}
            </th>
            <th scope="col" className={cn(HEAD, WIDE_ONLY)}>
              {t('reports.columns.purpose')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-right', WIDE_ONLY)}>
              {t('reports.columns.count')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-right whitespace-nowrap')}>
              {t('reports.columns.total')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-right', WIDE_ONLY)}>
              {t('reports.columns.reimbursable')}
            </th>
            <th scope="col" className={HEAD}>
              {t('reports.columns.status')}
            </th>
            <th scope="col" className={HEAD}>
              <span className="sr-only">{t('reports.columns.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((report) => (
            <ClickableRow
              key={report.id}
              className="border-border border-t"
              onOpen={() => void navigate(`/expenses/reports/${encodeURIComponent(report.id)}`)}
            >
              <td className="px-3 py-3 tabular-nums">
                <Link
                  to={`/expenses/reports/${encodeURIComponent(report.id)}`}
                  className="text-foreground font-semibold hover:underline"
                >
                  {report.number}
                </Link>
              </td>
              {showOwner ? (
                <td className={cn('px-3 py-3 whitespace-nowrap', WIDE_ONLY)}>
                  {report.owner.displayName}
                </td>
              ) : null}
              <td className="px-3 py-3">
                {report.title}
                {/* The tablet's folded columns: the person and the purpose under the reason. */}
                <span className={cn('text-muted-foreground block text-xs', COMPACT_ONLY)}>
                  {showOwner ? `${report.owner.displayName} · ` : null}
                  <ReportPurpose report={report} />
                </span>
              </td>
              <td className={cn('px-3 py-3', WIDE_ONLY)}>
                <ReportPurpose report={report} />
              </td>
              <td className={cn('px-3 py-3 text-right tabular-nums', WIDE_ONLY)}>
                {report.expenseCount}
              </td>
              <td className="px-3 py-3 text-right whitespace-nowrap tabular-nums">
                {formatAmount(report.total)}
              </td>
              <td className={cn('px-3 py-3 text-right tabular-nums', WIDE_ONLY)}>
                {formatAmount(report.reimbursable)}
              </td>
              <td className="px-3 py-3">
                <ReportStatusBadge status={report.status} />
              </td>
              <td className="px-1 py-1 text-right">
                <RowMenu report={report} pdf={pdf} />
              </td>
            </ClickableRow>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Phone (spec §1.2): a card per report; a draft says what is left to do. */
export function ReportCards({
  items,
  pdf,
  showOwner,
}: {
  readonly showOwner: boolean;
  readonly items: readonly ReportSummary[];
  readonly pdf: NotePdf;
}) {
  const { t } = useTranslation('expenses');
  const withMenus = items.some((report) => rowActions(t, report, pdf).length > 0);
  return (
    <ul aria-label={t('reports.title')} className="flex flex-col gap-2 @min-[620px]/shell:hidden">
      {items.map((report) => (
        <li key={report.id} className="border-border bg-card flex items-start rounded-xl border">
          <Link
            to={`/expenses/reports/${encodeURIComponent(report.id)}`}
            className="flex min-w-0 flex-1 items-start justify-between gap-3 p-3"
          >
            <span className="flex min-w-0 flex-col gap-1">
              <b className="tabular-nums">{report.number}</b>
              <small className="text-muted-foreground">
                {report.title} · {t('reports.cards.count', { count: report.expenseCount })}
                {/* The desktop table has «Utente»: with everyone's notes, the card names them. */}
                {showOwner ? ` · ${report.owner.displayName}` : null}
              </small>
              <small>
                <ReportPurpose report={report} />
              </small>
              <span className="flex flex-wrap items-center gap-1.5">
                <ReportStatusBadge status={report.status} />
                {report.status === 'draft' ? (
                  <small className="text-muted-foreground">{t('reports.cards.toComplete')}</small>
                ) : null}
              </span>
            </span>
            <b className="shrink-0 tabular-nums">{formatAmount(report.total)}</b>
          </Link>
          {withMenus ? (
            // The slot is kept on every card of a list that has menus, so the amounts line up.
            <span className="flex size-11 shrink-0 items-center justify-center self-start">
              <RowMenu report={report} pdf={pdf} touch />
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
